import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  BatchProgressBar,
  Button,
  Card,
  CardCloseButton,
  CardOptions,
  CheckboxField,
  ErrorMessage,
  FieldsetWrapper,
  NakedForm,
  PlayersSearch,
  SubmitButton,
  TextField,
  useModalContext,
} from '@/components';
import { useBatchProcessor } from '@/hooks';
import { Nullable } from '@/types';
import { assert } from '@/utils/error';

const query = graphql`
  query SanityChallengePlayerAssignForm {
    sanityChallengePlayerAssignForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const assignPlayersToChallengeMutation = gql`
  mutation AssignPlayersToChallenge(
    $playerIds: [String!]!
    $challengeId: String!
  ) {
    assignPlayersToChallenge(playerIds: $playerIds, challengeId: $challengeId)
  }
`;

type Props = {
  challengeId: string | undefined;
  challengeName: string | undefined;
};

type FormValues = {
  playerIds: string[];
  enableBatchProcessing?: boolean;
  batchSize?: number;
};

// Batch size for processing player assignments to a challenge.
// Change this value to adjust the batch size based on backend capabilities
const BATCH_SIZE = 1000;

const ChallengePlayerAssignForm = ({ challengeId, challengeName }: Props) => {
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const { close } = useModalContext();

  const staticData =
    useStaticQuery<Queries.SanityChallengePlayerAssignFormQuery>(query);

  const form = staticData.sanityChallengePlayerAssignForm;
  assert(form, 'missing form data');

  const [assignPlayersToChallengeState, assignPlayersToChallenge] = useMutation(
    assignPlayersToChallengeMutation,
  );

  const { processBatches, progress } = useBatchProcessor<string, boolean>({
    enableFeedback: true,
  });

  const defaultValues: FormValues = {
    playerIds: [],
    enableBatchProcessing: false,
    batchSize: BATCH_SIZE,
  };

  const methods = useForm<FormValues>({ defaultValues });
  const { watch, setValue } = methods;
  const watchedValues = watch();
  const { playerIds, enableBatchProcessing } = watchedValues;

  // Automatically enable batch processing if there are more than BATCH_SIZE player IDs
  useEffect(() => {
    if (playerIds && playerIds.length > BATCH_SIZE) {
      setValue('enableBatchProcessing', true);
    }
  }, [playerIds, setValue]);

  const handleCloseButton = useCallback(() => {
    if (close) {
      close();
    }
  }, [close]);

  const processPlayers = useCallback(
    async (batch: string[]): Promise<boolean> => {
      const variables = {
        challengeId,
        playerIds: batch,
      };

      const res = await assignPlayersToChallenge(variables);

      if (res.error?.message) {
        throw new Error(res.error.message);
      }

      return true;
    },
    [assignPlayersToChallenge, challengeId],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setErrorMessage(null);
      const batchSize = Number(values.batchSize) || BATCH_SIZE;

      try {
        await processBatches(values.playerIds, {
          batchSize,
          processor: (batch: string[]) => processPlayers(batch),
          fallbackThreshold: batchSize,
          fallbackProcessor: (playerIds: string[]) => processPlayers(playerIds),
        });

        // Close modal for normal submissions (non-batch processing)
        if (!values.enableBatchProcessing && close) {
          close();
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        setErrorMessage(errorMessage);
      }
    },
    [processBatches, processPlayers, close],
  );

  if (!form) {
    return null;
  }

  const hasPlayers = playerIds && playerIds.length > 0;
  const shouldShowBatchOptions = enableBatchProcessing && hasPlayers;
  const isBatchCompleted = !progress.isProcessing && progress.totalBatches > 0;

  const isProcessing =
    progress.isProcessing || assignPlayersToChallengeState.fetching;

  return (
    <Card
      size="lg"
      title={`Assign Player(s) to challenge: ${challengeName}`}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex p-6">
        <NakedForm
          className="w-full flex flex-col gap-4"
          methods={methods}
          onSubmit={onSubmit}
        >
          <div className="flex-1 sm:w-1/3">
            <PlayersSearch
              name="playerIds"
              id="ChallengePlayerAssignForm__playerIds"
              title="Players"
            />
          </div>
          <div className="flex-1 sm:w-1/2">
            <FieldsetWrapper legend="Batch Processing">
              <CheckboxField
                title="Enable batch processing for large player lists"
                name="enableBatchProcessing"
                id="RewardPlayerAddForm__enableBatchProcessing"
              />
              {shouldShowBatchOptions && (
                <TextField
                  name="batchSize"
                  id="RewardPlayerAddForm__batchSize"
                  title={`Batch size (default: ${BATCH_SIZE})`}
                  type="number"
                  min="1"
                  max="5000"
                />
              )}
            </FieldsetWrapper>
          </div>
          <BatchProgressBar progress={progress} className="mb-6" />
          <ErrorMessage message={errorMessage} />
          <div
            className={classNames('flex', {
              'justify-between': isBatchCompleted,
              'justify-end': !isBatchCompleted,
            })}
          >
            {isBatchCompleted && (
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={handleCloseButton}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Close
                </Button>
              </div>
            )}
            <SubmitButton
              value="Assign"
              disabled={isProcessing || isBatchCompleted || !hasPlayers}
            />
          </div>
        </NakedForm>
      </div>
    </Card>
  );
};

export default ChallengePlayerAssignForm;
